/**
 * useWebSocket — React hook nad `wsClient` singletonem (`src/lib/websocket.js`).
 *
 * Volá `wsClient.subscribe(topic, onEvent)` při mount, `unsubscribe` při
 * unmount nebo změně topicu. Conditional subscribe přes `enabled` flag —
 * typicky `!!user?.id` aby nešel WS subscribe před login.
 *
 * Příklad:
 *   useWebSocket(`user:${user?.id}:notifications`, (event) => {
 *     if (event.event === 'notification.new') refetchNotifications();
 *   }, !!user?.id);
 *
 * onEvent listener se cachuje přes useRef — komponenta může passnout
 * inline arrow function bez re-subscribe loop při každém renderu.
 */
import { useEffect, useRef } from 'react';

import { wsClient } from '@/lib/websocket';

/**
 * @param {string | null | undefined} topic - WS topic (např. "user:abc:notifications").
 *   Pokud null/undefined nebo enabled=false, hook neudělá subscribe.
 * @param {(event: {event: string, topic?: string, data?: unknown}) => void} onEvent
 * @param {boolean} [enabled=true]
 */
export function useWebSocket(topic, onEvent, enabled = true) {
  // Drž listener v ref aby effect závisel jen na topic, ne na callback identity.
  const listenerRef = useRef(onEvent);
  useEffect(() => {
    listenerRef.current = onEvent;
  }, [onEvent]);

  useEffect(() => {
    if (!enabled || !topic) return undefined;
    const sub = wsClient.subscribe(topic, (event) => {
      listenerRef.current?.(event);
    });
    return () => sub.unsubscribe();
  }, [topic, enabled]);
}
